import { createApi } from '@reduxjs/toolkit/query/react';
import { customBaseQuery } from '@/lib/baseQuery';
import { ApiResponse } from '@/shared/types';
import type {
  DismissResolution,
  DocumentSources,
  PortalDocument,
  PortalDocumentExtraction,
  PortalDocumentReadUrl,
  PortalRequeueResult,
} from '../types/portal-documents.type';

/** A slow answer becomes an error, so the Documents tab falls back to the FileInvite view. */
export const PORTAL_DOCUMENTS_TIMEOUT_MS = 15_000;

const BASE = '/v1/document-classification';

/**
 * The new (portal) document process for the CA Documents tab and the portal explorer
 * (spec 2026-10-07). JWT + the four mortgage roles on the backend. A mortgage_ems user gets
 * 403 here, which the tab reads as "show FileInvite, no warning".
 */
export const portalDocumentsApi = createApi({
  reducerPath: 'portalDocumentsApi',
  baseQuery: customBaseQuery,
  tagTypes: ['DocumentSources', 'PortalDocuments', 'PortalDocumentExtraction'],
  endpoints: (builder) => ({
    getDocumentSources: builder.query<DocumentSources, string>({
      query: (assessmentId) => ({
        url: `${BASE}/mortgage/assessments/${assessmentId}/document-sources`,
        timeout: PORTAL_DOCUMENTS_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<DocumentSources>) => r.data,
      providesTags: (_r, _e, id) => [{ type: 'DocumentSources', id }],
    }),
    listPortalDocuments: builder.query<PortalDocument[], string>({
      query: (assessmentId) => ({
        url: `${BASE}/documents`,
        params: { assessmentId, limit: 500 },
        timeout: PORTAL_DOCUMENTS_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<PortalDocument[]>) => r.data ?? [],
      providesTags: (_r, _e, id) => [{ type: 'PortalDocuments', id }],
    }),
    getPortalDocumentReadUrl: builder.query<PortalDocumentReadUrl, string>({
      query: (documentId) => ({
        url: `${BASE}/documents/${documentId}/read-url`,
        timeout: PORTAL_DOCUMENTS_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<PortalDocumentReadUrl>) => r.data,
      // Signed and short-lived: never reuse a cached one for long.
      keepUnusedDataFor: 30,
    }),
    getPortalDocumentExtraction: builder.query<PortalDocumentExtraction, string>({
      query: (documentId) => ({
        url: `${BASE}/documents/${documentId}/extraction`,
        timeout: PORTAL_DOCUMENTS_TIMEOUT_MS,
      }),
      transformResponse: (r: ApiResponse<PortalDocumentExtraction>) => r.data,
      providesTags: (_r, _e, documentId) => [{ type: 'PortalDocumentExtraction', id: documentId }],
    }),
    overridePortalDocument: builder.mutation<PortalRequeueResult, { documentId: string; assessmentId: string; docType: string }>({
      query: ({ documentId, docType }) => ({
        url: `${BASE}/documents/${documentId}/classification`,
        method: 'PATCH',
        body: { docType },
      }),
      transformResponse: (r: ApiResponse<PortalRequeueResult>) => r.data,
      invalidatesTags: (_r, _e, { assessmentId, documentId }) => [
        { type: 'PortalDocuments', id: assessmentId },
        { type: 'DocumentSources', id: assessmentId },
        { type: 'PortalDocumentExtraction', id: documentId },
      ],
    }),
    dismissPortalDocument: builder.mutation<void, { documentId: string; assessmentId: string; resolution: DismissResolution; note?: string }>({
      query: ({ documentId, resolution, note }) => ({
        url: `${BASE}/documents/${documentId}/dismiss`,
        method: 'POST',
        body: { resolution, ...(note ? { note } : {}) },
      }),
      invalidatesTags: (_r, _e, { assessmentId }) => [
        { type: 'PortalDocuments', id: assessmentId },
        { type: 'DocumentSources', id: assessmentId },
      ],
    }),
    requeuePortalDocument: builder.mutation<PortalRequeueResult, { documentId: string; assessmentId: string }>({
      query: ({ documentId }) => ({
        url: `${BASE}/documents/${documentId}/requeue`,
        method: 'POST',
      }),
      transformResponse: (r: ApiResponse<PortalRequeueResult>) => r.data,
      invalidatesTags: (_r, _e, { assessmentId, documentId }) => [
        { type: 'PortalDocuments', id: assessmentId },
        { type: 'DocumentSources', id: assessmentId },
        { type: 'PortalDocumentExtraction', id: documentId },
      ],
    }),
  }),
});

export const {
  useGetDocumentSourcesQuery,
  useListPortalDocumentsQuery,
  useGetPortalDocumentReadUrlQuery,
  useGetPortalDocumentExtractionQuery,
  useOverridePortalDocumentMutation,
  useDismissPortalDocumentMutation,
  useRequeuePortalDocumentMutation,
} = portalDocumentsApi;
